<template>
  <div class="test-input">
    <q-checkbox
      v-if="(showSelectionCheckbox && value) || (showSelectionCheckbox && value === null)"
      :value="isSelected"
      @input="onToggleSelection(keyMeta.name)"
      :dark="isDarkTheme"
      :disable="isDisplayOnly"
    />
    <div class="field" :style="standaloneStyle ? { backgroundColor: standaloneStyle } : ''">
      <div v-if="value === null" class="fake-input field" :name="keyMeta.name">
        <div class="fake-input__value"><q-icon name="remove_circle" /> null</div>
      </div>

      <json-input
        v-else-if="isJsonField"
        class="field"
        :label="keyMeta.name"
        :name="keyMeta.name"
        :value="value"
        :is-disabled="isDisabled"
        :data-id="keyMeta.name"
        :is-display-only="isDisplayOnly"
        @save="updateValue($event)"
        @focus="onFocus(keyMeta.name)"
        @blur="onBlur(keyMeta.name)"
      />

      <q-select
        v-else-if="isBooleanField"
        :dark="isDarkTheme"
        :style="isDarkTheme ? { backgroundColor: darkBackgroundColor } : ''"
        class="field"
        outlined
        dense
        :label="keyMeta.name"
        :value="value"
        :options="['True', 'False']"
        :disable="isDisabled || isDisplayOnly"
        :data-id="keyMeta.name"
        @input="updateValue"
        @focus="onFocus(keyMeta.name)"
        @blur="onBlur(keyMeta.name)"
        ><q-tooltip>
          <strong v-if="value"> {{ value }}</strong>
          (boolean)
        </q-tooltip></q-select
      >

      <q-input
        v-else-if="isFloatField"
        class="field"
        type="number"
        outlined
        color="info"
        dense
        :label="keyMeta.name"
        :value="value"
        :disable="isDisabled || isDisplayOnly"
        :data-id="keyMeta.name"
        @input="updateValue"
        @focus="onFocus(keyMeta.name)"
        @blur="onBlur(keyMeta.name)"
        :dark="isDarkTheme"
        :style="isDarkTheme ? { backgroundColor: darkBackgroundColor } : ''"
        ><q-tooltip>
          <strong v-if="value"> {{ value }}</strong>
          (float)
        </q-tooltip>
      </q-input>

      <q-input
        v-else-if="isIntField"
        class="field"
        type="number"
        outlined
        color="info"
        dense
        :label="keyMeta.name"
        :value="value"
        :disable="isDisabled || isDisplayOnly"
        :data-id="keyMeta.name"
        @input="val => updateValue(val.replace(/\./g, ''))"
        @focus="onFocus(keyMeta.name)"
        @blur="onBlur(keyMeta.name)"
        :dark="isDarkTheme"
        :style="isDarkTheme ? { backgroundColor: darkBackgroundColor } : ''"
        ><q-tooltip>
          <strong v-if="value"> {{ value }}</strong>
          (int)
        </q-tooltip>
      </q-input>

      <q-input
        v-else
        class="field"
        outlined
        dense
        :label="keyMeta.name"
        :data-id="keyMeta.name"
        :value="value"
        :disable="isDisabled || isDisplayOnly"
        @keyup.enter="isBeingMocked = false"
        @input="updateValue"
        @focus="onFocus(keyMeta.name)"
        @blur="onBlur(keyMeta.name)"
        :dark="isDarkTheme"
        :style="isDarkTheme ? { backgroundColor: darkBackgroundColor } : ''"
        ><q-tooltip>
          <strong v-if="value"> {{ value }}</strong>
          (string)
        </q-tooltip>
      </q-input>
      <q-tooltip v-if="!isDisplayOnly && !initiallyMockable && value === ''">
        This is a user function. You can mock its<br />
        value by selecting "Mock value" from the menu.
      </q-tooltip>
    </div>
    <q-btn v-if="!isDisplayOnly" icon="more_vert" size="md" flat dense style="width: 16px">
      <q-menu>
        <q-list style="min-width: 120px">
          <q-item
            v-if="!initiallyMockable && !isBeingMocked"
            clickable
            v-close-popup
            @click="updateValueAndStartMocking(valueAsString(keyMeta.defaultValue, keyMeta.type))"
          >
            <q-item-section>Mock value</q-item-section>
          </q-item>
          <q-item
            v-if="!initiallyMockable && isBeingMocked"
            clickable
            v-close-popup
            @click="updateValueAndStartMocking(undefined)"
          >
            <q-item-section>Run function</q-item-section>
          </q-item>
          <q-item v-if="value !== null" clickable v-close-popup @click="updateValueAndStartMocking(null)">
            <q-item-section>Nullify value</q-item-section>
          </q-item>
          <q-item
            v-if="initiallyMockable && value === null"
            clickable
            v-close-popup
            @click="updateValueAndStartMocking(undefined)"
          >
            <q-item-section>Set value</q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-btn>
  </div>
</template>
<script lang="ts">
import { KeyMeta } from '@/types'
import { valueAsString } from '@/utils/testValueUtils'
import { INPUT_BACKGROUND_COLOR_DARK, INPUT_BACKGROUND_COLOR_LIGHT, themesBackgroundColors } from '@/utils/themesUtils'
import { useDirectStore } from '@/utils/utils'
import { computed, defineComponent, ref } from 'vue'

interface Props {
  keyMeta: KeyMeta
  value: any
  isSelected: boolean
  showSelectionCheckbox: boolean
  updateValue: (val: string) => void
  onFocus: (keyName?: string) => void
  onBlur: (keyName?: string) => void
  onToggleSelection: (keyName?: string) => void
}

export default defineComponent<Props>({
  props: {
    isDisplayOnly: {
      type: Boolean,
      required: false,
    },
    keyMeta: {
      type: Object,
      required: true,
    },
    // eslint-disable-next-line vue/require-prop-types
    value: {
      required: true,
    },
    updateValue: {
      type: Function,
      required: true,
    },
    onFocus: {
      type: Function,
      default: () => undefined,
    },
    onBlur: {
      type: Function,
      default: () => undefined,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    showSelectionCheckbox: {
      type: Boolean,
      default: false,
    },
    onToggleSelection: {
      type: Function,
      default: () => undefined,
    },
  },
  setup(props) {
    const { getters, state } = useDirectStore()

    const isDarkTheme = computed(() => getters.themes.isDarkTheme)
    const darkBackgroundColor = computed(() => themesBackgroundColors[state.themes.themeName])
    const initiallyMockable = computed(() => props.keyMeta.initiallyMockable)

    const standaloneStyle = computed(() => {
      if (props.keyMeta.initiallyMockable) {
        return ''
      }

      return isDarkTheme.value ? INPUT_BACKGROUND_COLOR_DARK : INPUT_BACKGROUND_COLOR_LIGHT
    })
    const isBeingMocked = ref(false)
    const isDisabled = computed(() => !initiallyMockable.value && !isBeingMocked.value)

    return {
      standaloneStyle,
      isBeingMocked,
      isDisabled,
      initiallyMockable,
      valueAsString,
      isJsonField: computed(() => ['list', 'dict'].includes(props.keyMeta.type)),
      isBooleanField: computed(() => ['bool', 'boolean'].includes(props.keyMeta.type)),
      isIntField: computed(() => ['int', 'long'].includes(props.keyMeta.type)),
      isFloatField: computed(() => ['float', 'double'].includes(props.keyMeta.type)),
      updateValueAndStartMocking: function (value: any) {
        isBeingMocked.value = true
        props.updateValue(value)
      },
      isDarkTheme,
      darkBackgroundColor,
    }
  },
})
</script>
<style scoped lang="scss">
@import '@/styles/quasar.variables.scss';

.test-input {
  display: flex;
  flex: 1;
}

.field {
  flex: 1;
  overflow: hidden;
  max-width: 38vw;
}

.fake-input {
  position: relative;
  border-radius: 3px;
  border: 1px solid $grey-5;
  padding: $space-x-sm $space-y-sm;
  overflow: hidden;

  &:before {
    content: attr(name);
    font-size: 14px;
    transform: scale(0.75);
    transform-origin: left top;
    position: absolute;
    top: 0;
    left: $space-x-sm;
    color: $grey-8;
  }

  &__value {
    position: relative;
    bottom: -6px;
    max-height: 20px;

    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}
</style>
